import { useCallback, useState } from "react";
import { useIocContext } from "../contexts/ioc/IocContext";
import { Types } from "../ioc/types";
import { IOperationalService } from "../modules/operational/models/IOperationalService";
import { PaymentCondition } from "../modules/operational/models/GetPaymentConditionsDTO";
import useDialogAlert from "./DialogAlert";
import AppError from "../utils/appError";

export interface UseCustomerPaymentConditionsReturn {
  paymentConditionOptions: PaymentCondition[];
  loading: boolean;
  maxPaymentDays: number | null;
  fetchByDocument: (cnpj: string) => Promise<void>;
  reset: () => void;
}

function generatePaymentConditionOptions(maxDays: number): PaymentCondition[] {
  const options: PaymentCondition[] = [
    {
      code: "0000",
      description: "À vista",
    },
  ];

  for (let i = 1; i <= maxDays; i++) {
    const code = i.toString().padStart(4, "0");
    options.push({
      code,
      description: `${i} dia${i > 1 ? "s" : ""} (A prazo)`,
    });
  }

  return options;
}

export function useCustomerPaymentConditions(): UseCustomerPaymentConditionsReturn {
  const [paymentConditionOptions, setPaymentConditionOptions] = useState<
    PaymentCondition[]
  >([]);
  const [loading, setLoading] = useState(false);
  const [maxPaymentDays, setMaxPaymentDays] = useState<number | null>(null);

  const { snackbar } = useDialogAlert();
  const { serviceContainer } = useIocContext();
  const operationalService = serviceContainer.get<IOperationalService>(
    Types.Operational.IOperationalService
  );

  const fetchByDocument = useCallback(
    async (cnpj: string) => {
      try {
        setLoading(true);
        setPaymentConditionOptions([]);
        setMaxPaymentDays(null);

        const response = await operationalService.getCustomerPaymentConditions(
          cnpj
        );

        setMaxPaymentDays(response.maxPaymentDays);
        const options = generatePaymentConditionOptions(
          response.maxPaymentDays
        );
        setPaymentConditionOptions(options);
      } catch (error) {
        snackbar({
          message:
            (error as AppError)?.message ||
            "Erro ao carregar condições de pagamento do cliente",
          variant: "error",
        });
        setPaymentConditionOptions([]);
        setMaxPaymentDays(null);
      } finally {
        setLoading(false);
      }
    },
    [operationalService, snackbar]
  );

  const reset = useCallback(() => {
    setPaymentConditionOptions([]);
    setMaxPaymentDays(null);
    setLoading(false);
  }, []);

  return {
    paymentConditionOptions,
    loading,
    maxPaymentDays,
    fetchByDocument,
    reset,
  };
}
